import { useLogger } from "@kensho/lumberjack";
import { useInterval } from "@kensho/tacklebox";
import { useQuery } from "@tanstack/react-query";
import { Draft, produce, WritableDraft } from "immer";
import { cloneDeep, isEmpty, snakeCase, takeRight } from "lodash-es";
import {
  use,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";

import { useGetRevisionStatus } from "../api/use-get-revision-status";
import { useGetTranscriptRevisions } from "../api/use-get-revisions";
import { TranscriptPermissionsContext } from "../core/transcription/transcript-permissions-provider";
import { useRemoteTranscriptChanges } from "../core/transcription/transcript/use-remote-transcript-changes";
import { useMultiplayerContext } from "../hooks/use-multiplayer-context";
import { Operation, PersistedRevision, Revision } from "../types/schemas";
import {
  APITranscript,
  EditorAction,
  NullableEditorAction,
} from "../types/types";
import { applyRevision } from "../utils/apply-revision";
import { transformRevision } from "../utils/transform-revision";
import { transformSelection } from "../utils/transform-selection";
import type {
  RevisionCallback,
  RevisionMeta,
  RevisionSubscribe,
  TranscriptAction,
  TranscriptContextValue,
  TranscriptState,
} from "./transcript-context";
import { TranscriptContext } from "./transcript-context";
import { TranscriptionContext } from "./transcription-context";

const MAX_UNDO_HISTORY = 30;
const MAX_CACHED_REVISIONS = 500;
const PRUNE_INTERVAL_MS = 60 * 1000;
const MISSING_LATENCY_THRESHOLD_MS = 8 * 1000;
// This is a soft limit, a single revision could end up with more operations than this
// but a revision with this many operations already will not be considered as a batching target.
const MAX_BATCHED_OPERATIONS_SIZE = 20;

/** Rebases this editor action on top of the revision & inverse revision */
function transformEditorAction(
  editorAction: EditorAction,
  rev: Revision | null,
  inverseRev: Revision | null,
): {
  editorAction: EditorAction | null;
  rev: Revision | null;
  inverseRev: Revision | null;
} {
  const result: {
    editorAction: EditorAction | null;
    rev: Revision | null;
    inverseRev: Revision | null;
  } = { editorAction, rev, inverseRev };
  const originalEditorActionRev = editorAction.revision;
  const originalEditorActionInverseRev = editorAction.inverseRevision;
  // editor actions have a forward revision and an inverse revision. The result of the transforms
  // against each need to be carried through separately because they can only be applied separately
  let currentRev: Revision | null = rev;
  let currentInverseRev: Revision | null = inverseRev;

  const transformedEditorAction = produce<NullableEditorAction>(
    editorAction,
    (draftEditorAction) => {
      // transform editorAction so it is inclusive of current rev
      draftEditorAction.revision = transformRevision(
        currentRev,
        draftEditorAction.revision,
        true,
      );
      draftEditorAction.inverseRevision = transformRevision(
        currentInverseRev,
        draftEditorAction.inverseRevision,
        true,
      );

      // transform current rev against editorAction (untransformed) so that is inclusive of this editorAction
      // and will have context equivalence with the next one
      currentRev = transformRevision(
        originalEditorActionRev,
        currentRev,
        false,
      );
      currentInverseRev = transformRevision(
        originalEditorActionInverseRev,
        currentInverseRev,
        false,
      );

      // stored selections also need to be transformed because we're rebasing the action on top of this revision
      // we need to transform the selection against the revision after it has already been transformed by ours
      // example:
      // our action and the revision both delete tokens in the same range 5-10,
      // if we transform the selection against the revision that is not transformed the selection
      // is moved back 5 more tokens to an incorrect spot
      // but once we transform the rev (and it becomes null) it correctly stays where it is
      draftEditorAction.beforeSelection = transformSelection(
        draftEditorAction.beforeSelection,
        currentInverseRev,
      );
      draftEditorAction.afterSelection = transformSelection(
        draftEditorAction.afterSelection,
        currentInverseRev,
      );
    },
  );

  // if rebasing against the revision results in a noop then drop it
  // since a client can only undo its own changes, the server revision has made the undo inaccessible
  // example:
  // 1. client inserts character in token
  // 2. server deletes entire token
  // 3. client can't undo character in token that doesn't exist
  if (
    !transformEditorAction ||
    transformedEditorAction.revision === null ||
    transformedEditorAction.inverseRevision === null
  ) {
    result.editorAction = null;
  } else {
    result.editorAction = transformedEditorAction as EditorAction;
  }

  result.rev = currentRev;
  result.inverseRev = currentInverseRev;

  return result;
}

/**
 * Transforms the editorActions in the stack to effectively rebase them on top of the given revision.
 * Note that this function intentionally does not accept a priority argument.
 * Rebasing implies that the actions in the stack come after the revision therefore always have priority in a last-writer-wins model.
 */
function rebaseEditorActionStack(
  stack: EditorAction[],
  rev: Revision,
): EditorAction[] {
  // editor actions have a forward revision and an inverse revision. The result of the transforms
  // against each need to be carried through separately because they can only be applied separately
  let currentRev: Revision | null = cloneDeep(rev);
  let currentInverseRev: Revision | null = cloneDeep(rev);

  return (
    stack
      // iterate in reverse order because stacks are LIFO and the rev to rebase against needs
      // to be transformed against each previous editorAction before it can be transformed against the next one
      .reverse()
      .reduce<EditorAction[]>((acc, editorAction) => {
        const transformedEditorActionResult = transformEditorAction(
          editorAction,
          currentRev,
          currentInverseRev,
        );
        if (transformedEditorActionResult.editorAction)
          acc.push(transformedEditorActionResult.editorAction);

        currentRev = transformedEditorActionResult.rev;
        currentInverseRev = transformedEditorActionResult.inverseRev;

        return acc;
      }, [])
      .reverse()
  );
}

function updateTranscript(
  draftState: Draft<TranscriptState>,
  revision: Revision,
  revisionMeta: RevisionMeta,
): void {
  applyRevision(
    revision,
    draftState.transcript as WritableDraft<APITranscript>,
  );

  draftState.revisionEvents.push({ revision, revisionMeta });

  // transform undo and redo stack so they maintain context equivalence
  // only need to transform against server revisions because LIFO stack guarantees
  // undos and redos are always +1 version ahead of client revisions
  if (revisionMeta.fromServer) {
    draftState.undos = rebaseEditorActionStack(draftState.undos, revision);
    draftState.redos = rebaseEditorActionStack(draftState.redos, revision);
  }
}

function syncVersion(draftState: Draft<TranscriptState>): void {
  // in the majority of cases this should result in a +1 increment, however there are edge cases like
  // client revisions that were applied optimistically but then acked out of order with other remote revisions
  // could have created a situation where the version has lagged behind
  // catch up the version with all applied revisions from cachedRevisions
  while (draftState.cachedRevisions[draftState.version + 1]) {
    draftState.version += 1;
  }
}

function applyClientRevision(
  draftState: Draft<TranscriptState>,
  editorAction: Draft<EditorAction>,
  revisionMeta: RevisionMeta,
): void {
  let transformedEditorAction: Draft<EditorAction> | null = editorAction;
  // transform this editorAction against any server revisions that were applied first
  if (
    revisionMeta.baseVersion !== undefined &&
    !revisionMeta.isRedo &&
    !revisionMeta.isUndo &&
    revisionMeta.baseVersion < draftState.version
  ) {
    let version = revisionMeta.baseVersion;
    const missedServerRevisions: Revision[] = [];
    while (version <= draftState.version) {
      if (
        draftState.cachedRevisions[version] &&
        // only need server revisions, this action should already be inclusive of acked client revisions
        draftState.cachedRevisions[version].revisionMeta.fromServer === true &&
        draftState.cachedRevisions[version].revision
      ) {
        missedServerRevisions.push(
          draftState.cachedRevisions[version].revision as Revision,
        );
      }
      version += 1;
    }

    if (missedServerRevisions.length > 0) {
      transformedEditorAction =
        missedServerRevisions.reduce<EditorAction | null>(
          (acc, serverRevision) => {
            // if one transformation results in the editorAction becoming null it will stay null
            if (!acc) return acc;
            return transformEditorAction(acc, serverRevision, serverRevision)
              .editorAction;
          },
          transformedEditorAction,
        );
    }
  }

  if (!transformedEditorAction) return;

  const revision = revisionMeta.isUndo
    ? transformedEditorAction.inverseRevision
    : transformedEditorAction.revision;

  if (!revision) return;

  // optimistically apply revision to transcript
  updateTranscript(draftState, revision, { ...revisionMeta, fromClient: true });

  // try to find a queued revision that is not too big
  const batchRevision = draftState.clientRevisions
    // always skip the first one since we could have sent to the server already
    .slice(1)
    .find((rev) => rev && rev.operations.length < MAX_BATCHED_OPERATIONS_SIZE);
  if (batchRevision) {
    // fold these changes in to the existing revision
    batchRevision.operations = [
      ...batchRevision.operations,
      ...revision.operations,
    ];
  } else {
    // add revision to the queue to be sent to the server
    draftState.clientRevisions.push(revision);
  }
  // add to undo stack
  if (
    transformedEditorAction.undoable &&
    !revisionMeta.isUndo &&
    !revisionMeta.isRedo
  )
    draftState.undos = takeRight(
      [...draftState.undos, transformedEditorAction],
      MAX_UNDO_HISTORY,
    );
}

/**
 * Server revisions must be applied in contiguous version order.
 * Otherwise, the revision can not be transformed against queued concurrent client revisions.
 * Because operations can only be transformed against each other if they come from the same document context.
 */
function applyServerRevision(
  draftState: Draft<TranscriptState>,
  serverRevision: PersistedRevision,
): void {
  // can't apply revision if it is not the next one
  if (serverRevision.version !== draftState.version + 1)
    throw new Error(
      `Can't apply server revision at discontiguous version. Ours = ${draftState.version} Revision = ${serverRevision.version}`,
    );
  // transform server revision against concurrent queued client revisions
  let transformedServerRevision: Revision | null = cloneDeep(serverRevision);
  draftState.clientRevisions.forEach((clientRevision) => {
    transformedServerRevision = transformRevision(
      clientRevision,
      transformedServerRevision,
      true,
    );
  });
  // apply transformed server revision to transcript
  if (transformedServerRevision)
    updateTranscript(draftState, transformedServerRevision, {
      fromServer: true,
    });

  // rebase client revisions on top of server revision
  let currentServerRevision: Revision | null = cloneDeep(serverRevision);
  draftState.clientRevisions = draftState.clientRevisions.reduce<
    (Revision | null)[]
  >((acc, clientRevision, i) => {
    const transformedClientRevision = transformRevision(
      currentServerRevision,
      clientRevision,
      true,
    );
    currentServerRevision = transformRevision(
      clientRevision,
      currentServerRevision,
      false,
    );
    // if the revision was transformed to null omit from client revisions, unless it is
    // the first revision in the queue that we have already sent and are awaiting a server ack for
    if (
      transformedClientRevision !== null ||
      (i === 0 && draftState.status === "awaitingServerAck")
    )
      acc.push(transformedClientRevision);
    return acc;
  }, []);

  // remove from outstanding revisions and transfer to cachedRevisions now that it's been applied to local state
  delete draftState.outstandingRevisions[serverRevision.version];
  draftState.cachedRevisions[serverRevision.version] = {
    revision: transformedServerRevision,
    revisionMeta: { fromServer: true },
  };
  // update client's known version
  syncVersion(draftState);
  // check if there are more outstanding server revisions to apply
  if (draftState.outstandingRevisions[draftState.version + 1])
    applyServerRevision(
      draftState,
      draftState.outstandingRevisions[draftState.version + 1],
    );
}

function onServerRevisions(
  draftState: Draft<TranscriptState>,
  revisions: PersistedRevision[],
): void {
  // store all incoming revisions to be applied later
  revisions.forEach((serverRevision) => {
    // filter out our own revision that may have been processed already but not acked to us yet
    // otherwise that revision would get applied twice, since we've already applied once optimistically
    if (serverRevision.id === draftState.awaitingRevisionId) return;

    // filter out revisions we've already seen and applied
    if (serverRevision.version <= draftState.version) return;

    draftState.outstandingRevisions[serverRevision.version] = serverRevision;
  });
  // if the next version is available apply it immediately, otherwise the revisions came out-of-order
  // wait to catch up first
  const nextRevision = draftState.outstandingRevisions[draftState.version + 1];
  if (nextRevision) applyServerRevision(draftState, nextRevision);
}

/**
 * Server responds with ack with transformed revision.
 * If the revision resulted in a noop, then the ack returns null.
 */
function onServerAck(
  draftState: Draft<TranscriptState>,
  revision: PersistedRevision | null,
): void {
  draftState.lastAcked = revision;
  // store revision to use for keeping track of the version
  if (revision)
    draftState.cachedRevisions[revision.version] = {
      revision,
      revisionMeta: { fromClient: true },
    };
  // update client's version, no need to apply since this revision was already applied optimistically
  syncVersion(draftState);
  // remove from queue
  draftState.clientRevisions.shift();
  // trim revisions that were queued but transformed to null already
  draftState.clientRevisions = draftState.clientRevisions.filter(Boolean);
  draftState.status = "ready";
  draftState.awaitingRevisionId = null;
}

export function reducer(
  state: TranscriptState,
  action: TranscriptAction,
): TranscriptState {
  return produce(state, (draft) => {
    switch (action.type) {
      case "sentClientRevision":
        draft.status = "awaitingServerAck";
        draft.awaitingRevisionId = action.revisionId;
        return draft;
      case "clientEditorAction":
        applyClientRevision(draft, action.editorAction, {
          isUndo: false,
          isRedo: false,
          baseVersion: action.baseVersion,
        });
        // clear redo stack if performing another regular action
        if (action.editorAction.undoable) draft.redos = [];
        return draft;
      case "undo": {
        const undo = draft.undos.pop();
        if (undo) {
          applyClientRevision(draft, undo, { isUndo: true, isRedo: false });
          draft.redos.push(undo);
        }
        return draft;
      }
      case "redo": {
        const redo = draft.redos.pop();
        if (redo) {
          applyClientRevision(draft, redo, { isUndo: false, isRedo: true });
          draft.undos.push(redo);
        }
        return draft;
      }
      case "serverRevisions":
        onServerRevisions(draft, action.revisions);
        return draft;
      case "serverAck":
        onServerAck(draft, action.revision);
        return draft;
      case "prune":
        // remove cached revisions beyond MAX_CACHED_REVISIONS, older versions are removed first
        Object.keys(draft.cachedRevisions)
          .sort()
          .reverse()
          .slice(MAX_CACHED_REVISIONS)
          .forEach((version) => {
            delete draft.cachedRevisions[version];
          });
        return draft;
      case "consumeRevisionEvents":
        draft.revisionEvents = draft.revisionEvents.slice(action.toIndex + 1);
        return draft;
      default:
        return draft;
    }
  });
}

function useRevisionEvents({
  revisionEvents,
  dispatch,
}: {
  revisionEvents: TranscriptState["revisionEvents"];
  dispatch: React.ActionDispatch<[action: TranscriptAction]>;
}): RevisionSubscribe {
  const revisionCallbacksRef = useRef<Set<RevisionCallback>>(new Set());

  // invoke each callback with each new revision event
  useEffect(() => {
    if (revisionEvents.length === 0) return;

    revisionEvents.forEach((revisionEvent) => {
      revisionCallbacksRef.current.forEach((callback) => {
        callback(revisionEvent);
      });
    });

    // clear events now that they have been propagated
    dispatch({
      type: "consumeRevisionEvents",
      toIndex: revisionEvents.length - 1,
    });
  }, [revisionEvents, dispatch]);

  // return function to register a revision event listener that will be wrapped into
  // the transcript context for call sites to use
  return useCallback((callback: RevisionCallback): (() => void) => {
    revisionCallbacksRef.current.add(callback);
    return () => revisionCallbacksRef.current.delete(callback);
  }, []);
}

interface RevisionRange {
  start: number;
  end: number;
}

function useMissingRevisions({
  transcriptId,
  version,
  outstandingRevisions,
  dispatch,
}: {
  transcriptId: string;
  version: TranscriptState["version"];
  outstandingRevisions: TranscriptState["outstandingRevisions"];
  dispatch: React.ActionDispatch<[action: TranscriptAction]>;
}): void {
  // get the contiguous range of revisions that are missing from the client's state.version + 1
  // up to the next lowest outstanding revision's version
  const missingRevisionRange = useMemo<RevisionRange | null>(() => {
    if (isEmpty(outstandingRevisions)) return null;
    if (outstandingRevisions[version + 1]) return null;
    return {
      start: version + 1,
      end:
        Object.values(outstandingRevisions)
          .map((rev) => rev.version)
          .sort()[0] - 1,
    };
  }, [version, outstandingRevisions]);
  const isMissingRange = !!missingRevisionRange;

  // give a few seconds before determining the revisions are truly MIA because
  // they may still be in-flight and moments away from receipt
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    if (!isMissingRange) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setEnabled(false);
      return undefined;
    }
    const timeoutId = window.setTimeout(() => {
      setEnabled(true);
    }, MISSING_LATENCY_THRESHOLD_MS);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [version, isMissingRange]);

  // fetch missing revisions
  const getTranscriptRevisions = useGetTranscriptRevisions();
  const { error } = useQuery({
    queryKey: ["transcriptRevisions", transcriptId, missingRevisionRange],
    enabled: enabled && isMissingRange,
    queryFn: async ({ signal }) => {
      if (!missingRevisionRange) return null;
      const revisions = await getTranscriptRevisions(
        { transcriptId, ...missingRevisionRange },
        signal,
      );
      // add to local state
      dispatch({
        type: "serverRevisions",
        revisions: Object.values(revisions),
      });
      return revisions;
    },
    retry: 3,
    retryDelay: (attempt) => attempt * 1000,
  });

  // switch to read-only mode if missing revisions fail to sync
  const { setTranscriptPermissions } = use(TranscriptPermissionsContext);
  const log = useLogger();
  useEffect(() => {
    if (!error) return;
    setTranscriptPermissions({ edit: false });
    log.error(error);
  }, [error, log, setTranscriptPermissions]);
}

function useMissingAck({
  transcriptId,
  status,
  awaitingRevisionId,
  dispatch,
}: {
  transcriptId: string;
  status: TranscriptState["status"];
  awaitingRevisionId: TranscriptState["awaitingRevisionId"];
  dispatch: React.ActionDispatch<[action: TranscriptAction]>;
}): void {
  // give a few seconds before determining the ack is truly MIA because
  // it may still be in-flight and moments away from receipt
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    if (status !== "awaitingServerAck") {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setEnabled(false);
      return undefined;
    }
    const timeoutId = window.setTimeout(() => {
      setEnabled(true);
    }, MISSING_LATENCY_THRESHOLD_MS);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [
    status,
    // unused within closure but included as dep so that if ack happens and
    // next client revision is sent without changing status then the timeout is reset
    awaitingRevisionId,
  ]);

  // fetch status of sent revision
  const getRevisionStatus = useGetRevisionStatus();
  const { error } = useQuery({
    queryKey: [
      "transcriptRevisions",
      transcriptId,
      awaitingRevisionId,
      "status",
    ],
    enabled: enabled && status === "awaitingServerAck" && !!awaitingRevisionId,
    queryFn: async ({ signal }) => {
      if (!awaitingRevisionId) return null;
      const response = await getRevisionStatus(
        { transcriptId, revisionId: awaitingRevisionId },
        signal,
      );

      switch (response.status) {
        case "complete":
          dispatch({ type: "serverAck", revision: response.revision });
          break;
        case "error":
          throw new Error(
            `Client revision failed to process: ${awaitingRevisionId}`,
          );
        case "in_progress":
        default:
          break;
      }

      return response;
    },
    retry: 3,
    retryDelay: (attempt) => attempt * 1000,
  });

  // switch to read-only mode if missing ack fails to sync
  const { setTranscriptPermissions } = use(TranscriptPermissionsContext);
  const log = useLogger();
  useEffect(() => {
    if (!error) return;
    setTranscriptPermissions({ edit: false });
    log.error(error);
  }, [error, log, setTranscriptPermissions]);
}

export function TranscriptProvider(props: {
  children: React.ReactNode;
  transcriptId: string;
  initialTranscript: APITranscript;
  initialVersion: number;
}) {
  const { children, transcriptId, initialTranscript, initialVersion } = props;
  const { stage } = use(TranscriptionContext);
  const { transcriptPermissions } = use(TranscriptPermissionsContext);

  const [state, dispatch] = useReducer(reducer, {
    version: initialVersion,
    transcript: initialTranscript,
    status: "ready",
    awaitingRevisionId: null,
    lastAcked: null,
    clientRevisions: [],
    outstandingRevisions: {},
    cachedRevisions: {},
    revisionEvents: [],
    undos: [],
    redos: [],
  });

  const { sendMessage, sessionId } = useMultiplayerContext((message) => {
    if (message.type !== "ack-revision") return;
    const { payload } = message;
    const { id, revision } = payload;
    // if server took a very long time and delivers the ack after we already manually retrieved it
    // because we thought it was missing, checking the id prevents acking the wrong one
    if (id === state.awaitingRevisionId)
      dispatch({ type: "serverAck", revision });
  });

  useRemoteTranscriptChanges(dispatch);

  const sendRevision = useCallback(
    (revision: Revision): void => {
      if (!sessionId) return;

      // Two differences to convert for revisions stored on the client vs what the server expects:
      // 1. The parts of operation paths need to be converted to snake_case
      // 2. The shape, including non-primitive values, needs to be converted to snake_case
      const convertedRevision = {
        ...revision,
        operations: revision.operations.map((operation) => {
          if ("path" in operation) {
            return {
              ...operation,
              path: operation.path.map((part) =>
                typeof part === "string" && !Number(part)
                  ? snakeCase(`${part}`)
                  : part,
              ),
            } as Operation;
          }
          return operation;
        }),
      };

      if (convertedRevision.version === undefined)
        throw new Error(
          `Attempted to send revision without a version but version is required.`,
        );
      if (convertedRevision.id === undefined)
        throw new Error(
          `Attempted to send revision without a id but id is required.`,
        );

      sendMessage({
        type: "revision",
        payload: {
          id: convertedRevision.id,
          operations: convertedRevision.operations,
          version: convertedRevision.version,
        },
      });
      return;
    },
    [sendMessage, sessionId],
  );

  // when ready, send the next revision in the queue to the server to be transformed and persisted
  useEffect(() => {
    if (!transcriptPermissions.edit) return;
    if (!(state.status === "ready" && state.clientRevisions[0])) return;
    // can't have any gaps before sending our next revision
    if (state.lastAcked && state.version < state.lastAcked.version) return;

    if (stage === "POST_TRANSCRIPTION" && sessionId) {
      const clientRevision = cloneDeep(state.clientRevisions[0]);
      clientRevision.id = crypto.randomUUID();
      // assign the target version so server knows how to transform against concurrent operations
      clientRevision.version = state.version + 1;
      // only send one revision at a time to maintain single state space between server and many clients
      sendRevision(clientRevision);
      dispatch({ type: "sentClientRevision", revisionId: clientRevision.id });
    }
  }, [
    state,
    sendMessage,
    stage,
    transcriptPermissions.edit,
    sessionId,
    sendRevision,
  ]);

  useMissingRevisions({
    transcriptId,
    version: state.version,
    outstandingRevisions: state.outstandingRevisions,
    dispatch,
  });

  useMissingAck({
    transcriptId,
    status: state.status,
    awaitingRevisionId: state.awaitingRevisionId,
    dispatch,
  });

  const registerRevisionCallback = useRevisionEvents({
    revisionEvents: state.revisionEvents,
    dispatch,
  });

  useInterval(() => dispatch({ type: "prune" }), PRUNE_INTERVAL_MS);

  const value = useMemo<TranscriptContextValue>(
    () => ({
      ...state,
      dispatch,
      registerRevisionCallback,
    }),
    [state, dispatch, registerRevisionCallback],
  );

  return (
    <TranscriptContext.Provider value={value}>
      {children}
    </TranscriptContext.Provider>
  );
}
